import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, FileText } from "lucide-react";
import { PortalHeader, PortalCard, PortalEmpty } from "@/components/portal/PortalHeader";
import { getMyInvoices } from "@/lib/accounting/accounting.functions";
import { fmtDate, fmtMoney } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/portal/invoices")({
  component: PortalInvoices,
});

function PortalInvoices() {
  const fn = useServerFn(getMyInvoices);
  const { data, isLoading } = useQuery({ queryKey: ["portal", "invoices"], queryFn: () => fn() });

  if (isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;

  return (
    <>
      <PortalHeader eyebrow="Invoices" title="Your invoices" description="Tap any invoice to view details, download a copy, or see payment history." />
      {(!data || data.length === 0) ? (
        <PortalEmpty title="No invoices yet" description="Your invoices will appear here as your project progresses." />
      ) : (
        <div className="space-y-2">
          {data.map((i: any) => (
            <Link key={i.id} to="/portal/invoices/$id" params={{ id: i.id }}>
              <PortalCard className="p-4 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3">
                <div className="size-10 rounded-xl bg-white/5 grid place-items-center shrink-0"><FileText className="size-4 text-amber-300" /></div>
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium font-mono">{i.invoice_number}</p>
                  <p className="text-[11px] text-zinc-500">{fmtDate(i.issue_date)} · due {fmtDate(i.due_date)} · <span className="capitalize">{i.status.replace("_", " ")}</span></p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white font-semibold">{fmtMoney(i.total)}</p>
                  <p className={`text-[11px] ${Number(i.balance_due) > 0 ? "text-amber-300" : "text-emerald-300"}`}>
                    {Number(i.balance_due) > 0 ? `${fmtMoney(i.balance_due)} due` : "Paid"}
                  </p>
                </div>
              </PortalCard>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}